import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import * as Icon from 'react-feather'
import useAxios from 'axios-hooks'
import _ from 'lodash'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { NumberParam, StringParam, withDefault } from 'use-query-params'
import { Nav, NavItem, TabContent, TabPane, Spinner } from 'reactstrap'
import { FormattedMessage, useIntl } from 'react-intl'

// import SearchInputImperative from '~/components/ecto/SearchInputImperative'
import Breadcrumb from '~/components/Breadcrumb'
import useSelectedProject from '~/hooks/useSelectedProject'
import { ReputationPage } from './components/ReputationPage'
import { EngagementPage } from './components/EngagementPage'
import { BehaviorPage } from './components/BehaviorPage'
import {
  Body,
  KeywordsHeader,
  ButtonTemplate,
  KeywordsContainer,
  ContentWrapper,
  SessionHeroContainer,
} from './styles'
import FilterButton from '~/components/FilterButton'
import DefaultFilterHandler from '~/components/Filters/Default/Handler'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import DatePicker from './components/DatePicker'

const COLUMN_ID = {
  location: 'location.name',
  questions: 'questions',
  reviews: 'reviews',
  average: 'average',
  'one-star': 'onestar',
  'two-star': 'twostar',
  'three-star': 'threestar',
  'four-star': 'fourstar',
  'five-star': 'fivestar',
  direction: 'direction',
  calls: 'calls',
  website: 'website',
  googleSearch: 'googlesearchtotal',
  googleMaps: 'googlemapstotal',
}

const TAB_ID = ['reputation', 'engagement', 'behavior']

const InsightsLocations = () => {
  const intl = useIntl()
  const projectId = useSelectedProject(project => project?.id)
  const [loading, setLoading] = useState([])
  const [downloading, setDownloading] = useState(false)
  const [filterOn, setFilterOn] = React.useState(false)
  const { query, setQuery, setQueries } = useSearchParamsHandler()

  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
  })

  const setLimitParams = limit => {
    setQueries({ page: 1, limit })
  }

  const setPageParams = page => {
    setQuery('page', page)
  }

  const setSortParams = (selectedColumn, sortDirection) => {
    setQueries({
      sort: COLUMN_ID[selectedColumn.id],
      order: sortDirection,
    })
  }

  const [
    {
      data: { locations: reputationData, count: reputationTotal } = {
        locations: [],
        count: 0,
      },
      loading: loadingReputation,
    },
    fetchReputation,
  ] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/reputation`,
      params: {
        ...query,
        period:
          !query.period && projectId === '7f77bb82-2e71-4848-b13b-be245cab9cda'
            ? '2024-07'
            : query.period,
      },
    },
    { useCache: true, manual: true }
  )

  const [
    {
      data: { locations: engagementData, count: engagementTotal } = {
        locations: [],
        count: 0,
      },
      loading: loadingEngagement,
    },
    fetchEngagement,
  ] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/engagement`,
      params: {
        ...query,
        period:
          !query.period && projectId === '7f77bb82-2e71-4848-b13b-be245cab9cda'
            ? '2024-07'
            : query.period,
      },
    },
    { useCache: true, manual: true }
  )

  const [
    {
      data: { locations: behaviorData, count: behaviorTotal } = {
        locations: [],
        count: 0,
      },
      loading: loadingBehavior,
    },
    fetchBehavior,
  ] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/behavior`,
      params: {
        ...query,
        period:
          !query.period && projectId === '7f77bb82-2e71-4848-b13b-be245cab9cda'
            ? '2024-07'
            : query.period,
      },
    },
    { useCache: true, manual: true }
  )

  const [, downloadKeywordsCSV] = useAxios(
    {
      method: 'GET',
      url: `/projects/insights/${projectId}/locations/csv`,
      params: {
        period:
          !query.period && projectId === '7f77bb82-2e71-4848-b13b-be245cab9cda'
            ? '2024-07'
            : query.period,
      },
    },
    { useCache: true, manual: true }
  )

  const handleFetchReputation = useCallback(
    _.debounce(() => fetchReputation(), 250),
    [fetchReputation]
  )

  const handleFetchEngagement = useCallback(
    _.debounce(() => fetchEngagement(), 250),
    [fetchEngagement]
  )

  const handleFetchBehavior = useCallback(
    _.debounce(() => fetchBehavior(), 250),
    [fetchBehavior]
  )

  function handleNavTabs(id) {
    const tabsNav = document.querySelector('.nav.nav-tabs')
    const tabContent = document.querySelector('.tab-content')
    const panes = tabContent.childNodes
    const tabs = tabsNav.childNodes
    tabs.forEach((tab, index) => {
      if (index === id) return tab.classList.add('active')
      tab.classList.remove('active')
    })
    panes.forEach((pane, index) => {
      if (index === id) return pane.classList.add('active')
      pane.classList.remove('active')
    })
  }

  function handleNavClick(id) {
    if (TAB_ID[id] === query.tab) return
    setLoading(true)
    handleNavTabs(id)
    setQueries({
      tab: TAB_ID[id],
      page: 1,
      sort: null,
      order: null,
    })
  }

  const downloadCSV = async () => {
    setDownloading(true)
    const { period } = query
    downloadKeywordsCSV({
      params: {
        period,
      },
    })
      .then(({ data }) => {
        const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8,' })
        const blobURL = window.URL.createObjectURL(blob)
        // Create new tag for download file
        const anchor = document.createElement('a')
        anchor.download = 'insights-locations-insights.csv'
        anchor.href = blobURL
        anchor.dataset.downloadurl = [
          'text/csv',
          anchor.download,
          anchor.href,
        ].join(':')
        anchor.click()
        // Remove URL.createObjectURL. The browser should not save the reference to the file.
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobURL)
        }, 100)
      })
      .catch(e => {
        toast.error('Error downloading your CSV file.')
        console.error(e)
      })
      .finally(() => setDownloading(false))
  }

  useEffect(() => {
    if (!loadingReputation && !loadingEngagement && !loadingBehavior)
      setLoading(false)
  }, [loadingReputation, loadingEngagement, loadingBehavior])

  useEffect(() => {
    if (query.tab === 'reputation' || !query.tab) {
      handleFetchReputation(0)
      handleNavTabs(0)
    }
    if (query.tab === 'engagement') {
      handleFetchEngagement(1)
      handleNavTabs(1)
    }
    if (query.tab === 'behavior') {
      handleFetchBehavior(2)
      handleNavTabs(2)
    }
  }, [
    handleFetchReputation,
    handleFetchEngagement,
    handleFetchBehavior,
    projectId,
    query,
  ])

  return (
    <>
      <KeywordsHeader>
        <Breadcrumb
          paths={[
            {
              render: () => (
                <Link to="/">
                  <Icon.Home size={20} />
                </Link>
              ),
            },
            { text: 'Insights' },
            { text: 'Locations' },
          ]}
        />

        <div className="d-flex align-items-center">
          <div>{intl.formatMessage({ id: 'Export to' })}:</div>
          <div className="buttons--container">
            <ButtonTemplate
              className="ml-2 border-0"
              onClick={() => downloadCSV()}
              disabled={downloading}
            >
              {downloading && (
                <Spinner
                  color="#fff"
                  size="sm"
                  style={{ marginRight: '1.3em' }}
                />
              )}
              {!downloading && (
                <Icon.Download height={14} className="d-none d-md-block" />
              )}
              <FormattedMessage id="CSV" />
            </ButtonTemplate>
          </div>
        </div>
      </KeywordsHeader>
      <SessionHeroContainer>
        <div className="wrapper">
          <h2>
            <FormattedMessage id="Locations" />
          </h2>
          <div className="buttons">
            <DatePicker />
            <FilterButton
              isActive={filterOn}
              filterCount={filterCount}
              onClick={() => setFilterOn(prevState => !prevState)}
            />
            <DefaultFilterHandler
              open={filterOn}
              onClose={() => setFilterOn(false)}
            />
          </div>
        </div>
      </SessionHeroContainer>

      <KeywordsContainer>
        <ContentWrapper>
          <Body>
            <StyledNav tabs>
              <NavItem className="active" onClick={() => handleNavClick(0)}>
                {intl.formatMessage({ id: 'Reputation' })}
              </NavItem>
              <NavItem onClick={() => handleNavClick(1)}>
                {intl.formatMessage({ id: 'Engagement' })}
              </NavItem>
              <NavItem onClick={() => handleNavClick(2)}>
                {intl.formatMessage({ id: 'Behavior' })}
              </NavItem>
            </StyledNav>

            <TabContent activeTab="1">
              <TabPane tabId="1">
                <ReputationPage
                  data={reputationData}
                  total={reputationTotal}
                  loading={loadingReputation || loading}
                  setLimitParams={setLimitParams}
                  setPageParams={setPageParams}
                  setSortParams={setSortParams}
                />
              </TabPane>
              <TabPane tabId="2">
                <EngagementPage
                  data={engagementData}
                  total={engagementTotal}
                  loading={loadingEngagement || loading}
                  setLimitParams={setLimitParams}
                  setPageParams={setPageParams}
                  setSortParams={setSortParams}
                />
              </TabPane>
              <TabPane tabId="3">
                <BehaviorPage
                  data={behaviorData}
                  total={behaviorTotal}
                  loading={loadingBehavior || loading}
                  setLimitParams={setLimitParams}
                  setPageParams={setPageParams}
                  setSortParams={setSortParams}
                />
              </TabPane>
            </TabContent>
          </Body>
        </ContentWrapper>
      </KeywordsContainer>
    </>
  )
}

const Wrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )
  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
        tab: StringParam,
        page: NumberParam,
        limit: NumberParam,
        period: StringParam,
        status: StringParam,
        sort: StringParam,
        order: StringParam,
      }}
    >
      <InsightsLocations />
    </SearchParamsHandlerProvider>
  )
}

const StyledNav = styled(Nav)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 2em;
  align-items: center;
  box-shadow: none !important;
  overflow-x: auto;
  overflow-y: hidden;

  > li {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: auto;
    height: 100%;
    font-size: 1.1rem;
    padding: 1em 1.5em;
    color: #d2d2ff;
    cursor: pointer;
  }

  > li.active {
    color: #0091ff;
    border-bottom: 3px solid #0091ff;
  }
`

InsightsLocations.displayName = 'Insights Locations Page'

export default Wrapper
